<app-section-grid *ngIf="!embeddedMode" [subSection]="'Create User'"></app-section-grid>

<app-stepper-grid *ngIf="!embeddedMode"></app-stepper-grid>

<div
  class="relative mx-auto mb-5 w-full rounded-3xl bg-white p-5 sm:p-8 lg:p-10"
  [class.border]="hasBorder"
  [class.border-gray-300]="hasBorder"
  style="max-width: 760px;"
>
  <form id="userForm" [formGroup]="userForm" (ngSubmit)="onSubmit()">
    <!-- HEADER -->
    <div class="mb-10 text-center">
      <h1 class="text-3xl font-bold tracking-tight text-gray-800">
        User Details
      </h1>

      <p class="mt-2 text-sm text-gray-500">
        Enter the user details below.
      </p>
    </div>

    <!-- FIELDS -->
    <div class="grid grid-cols-12 gap-6">
      <!-- EMAIL -->
      <div class="col-span-12">
        <label class="mb-2 block text-sm font-semibold text-gray-700">
          Email <small class="text-red-300">(required)</small>
        </label>

        <div class="relative">
          <input
            formControlName="Email"
            type="email"
            autocomplete="off"
            readonly
            onfocus="this.removeAttribute('readonly');"
            class="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-2.5 text-sm text-gray-700 transition-colors placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <p
          *ngIf="getControl('Email')?.hasError('required') && getControl('Email')?.touched"
          class="mt-1 text-xs text-red-500"
        >
          Email is required.
        </p>

        <p
          *ngIf="getControl('Email')?.hasError('email') && getControl('Email')?.touched"
          class="mt-1 text-xs text-red-500"
        >
          Please enter a valid email address.
        </p>
      </div>

      <!-- PASSWORD -->
      <div *ngIf="!formData.ID" class="col-span-12 lg:col-span-6">
        <label class="mb-2 block text-sm font-semibold text-gray-700">
          Password <small class="text-red-300">(required)</small>
        </label>

        <div class="relative">
          <input
            formControlName="Password"
            [type]="hidePassword() ? 'password' : 'text'"
            autocomplete="off"
            class="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-2.5 pr-12 text-sm text-gray-700 transition-colors placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          <button
            type="button"
            class="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
            (click)="toggleVisibility('password', $event)"
          >
            {{ hidePassword() ? '👁️' : '👁️‍🗨️' }}
          </button>
        </div>

        <p
          *ngIf="getControl('Password')?.hasError('required') && getControl('Password')?.touched"
          class="mt-1 text-xs text-red-500"
        >
          Password is required.
        </p>

        <p
          *ngIf="getControl('Password')?.hasError('minlength') && getControl('Password')?.touched"
          class="mt-1 text-xs text-red-500"
        >
          Password must be at least 6 characters.
        </p>
      </div>

      <!-- CONFIRM PASSWORD -->
      <div *ngIf="!formData.ID" class="col-span-12 lg:col-span-6">
        <label class="mb-2 block text-sm font-semibold text-gray-700">
          Confirm Password <small class="text-red-300">(required)</small>
        </label>

        <div class="relative">
          <input
            formControlName="ConfirmPassword"
            [type]="hideConfirmPassword() ? 'password' : 'text'"
            autocomplete="off"
            class="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-2.5 pr-12 text-sm text-gray-700 transition-colors placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          <button
            type="button"
            class="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
            (click)="toggleVisibility('confirmPassword', $event)"
          >
            {{ hideConfirmPassword() ? '👁️' : '👁️‍🗨️' }}
          </button>
        </div>

        <p
          *ngIf="getControl('ConfirmPassword')?.hasError('passwordMismatch')"
          class="mt-1 text-xs text-red-500"
        >
          Passwords do not match. <small class="text-red-300">(required)</small>
        </p>
      </div>

      <!-- FIRST NAME -->
      <div class="col-span-12 lg:col-span-6">
        <label class="mb-2 block text-sm font-semibold text-gray-700">
          First Name <small class="text-red-300">(required)</small>
        </label>

        <input
          formControlName="FirstName"
          class="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-2.5 text-sm text-gray-700 transition-colors placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        <p
          *ngIf="getControl('FirstName')?.hasError('required') && getControl('FirstName')?.touched"
          class="mt-1 text-xs text-red-500"
        >
          First Name is required.
        </p>
      </div>

      <!-- LAST NAME -->
      <div class="col-span-12 lg:col-span-6">
        <label class="mb-2 block text-sm font-semibold text-gray-700">
          Last Name <small class="text-red-300">(required)</small>
        </label>

        <input
          formControlName="LastName"
          class="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-2.5 text-sm text-gray-700 transition-colors placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        <p
          *ngIf="getControl('LastName')?.hasError('required') && getControl('LastName')?.touched"
          class="mt-1 text-xs text-red-500"
        >
          Last Name is required.
        </p>
      </div>

      <!-- PHONE -->
      <div class="col-span-12 lg:col-span-4">
        <label class="mb-2 block text-sm font-semibold text-gray-700">
          Phone <small class="text-red-300">(required)</small>
        </label>

        <input
          formControlName="Phone"
          class="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-2.5 text-sm text-gray-700 transition-colors placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        <p
          *ngIf="getControl('Phone')?.hasError('required') && getControl('Phone')?.touched"
          class="mt-1 text-xs text-red-500"
        >
          Phone is required.
        </p>

        <p
          *ngIf="getControl('Phone')?.invalid && getControl('Phone')?.touched"
          class="mt-1 text-xs text-red-500"
        >
          Phone must be exactly 10 digits.
        </p>
      </div>

      <!-- USER GROUP -->
      <div class="col-span-12 lg:col-span-4">
        <label class="mb-2 block text-sm font-semibold text-gray-700">
          User Group <small class="text-red-300">(required)</small>
        </label>

        <select
          formControlName="UserGroupID"
          class="w-full cursor-pointer rounded-xl border border-gray-300 bg-gray-50 px-4 py-2.5 text-sm text-gray-700 transition-colors focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option *ngFor="let userGroup of userGroups" [value]="userGroup.ID">
            {{ userGroup.Name }}
          </option>
        </select>
      </div>

      <!-- COMPANY -->
      <div class="col-span-12 lg:col-span-4">
        <label class="mb-2 block text-sm font-semibold text-gray-700">
          Company
        </label>

        <select
          *ngIf="!isLoadingCompanies"
          formControlName="companyID"
          [disabled]="companyId !== null"
          class="w-full cursor-pointer rounded-xl border border-gray-300 bg-gray-50 px-4 py-2.5 text-sm text-gray-700 transition-colors focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
        >
          <option value="">Select Company</option>
          <option *ngFor="let company of companies" [value]="company.ID">
            {{ company.Name }}
          </option>
        </select>
      </div>
    </div>

    <!-- SUBMIT -->
    <div class="mt-10 border-t border-gray-200 pt-6">
      <app-submit-grid></app-submit-grid>
    </div>
  </form>
</div>

<app-footer-grid *ngIf="!embeddedMode"></app-footer-grid>